/**
 * Single source of truth for every WhatsApp (wa.me) link on the site.
 *
 * Every link must open with a pre-filled first-person message naming the topic
 * and page, so the enquiry arrives with context instead of a blank chat.
 * Messages stay in English on every locale (the sales team reads them).
 *
 * tests/whatsapp-links.spec.ts fails if a `wa.me/` URL is hardcoded anywhere
 * else in source.
 */

/** Malaysia WhatsApp Business number, digits only (wa.me format). */
export const WHATSAPP_NUMBER = "601159246128";

/** Human-readable form of WHATSAPP_NUMBER for visible link text. */
export const WHATSAPP_DISPLAY = "+6011-5924-6128";

/** Build a wa.me link with a pre-filled message. */
export function whatsappHref(message: string): string {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

/** "Hi Anchor Sprint, <body> (from <source>)." */
export function whatsappMessage(body: string, source: string): string {
  return `Hi Anchor Sprint, ${body} (from ${source}).`;
}

// Floating-button messages keyed by locale-less route prefix. Longest prefix wins,
// so specific pages can sit beside their section.
const FLOATING_MESSAGES: Array<[prefix: string, body: string, source: string]> = [
  ["/services/ai-agents", "I'd like to talk about building an AI agent", "your AI Agents page"],
  ["/services/ai-solutions", "I'd like to talk about an AI solution for my business", "your AI Solutions page"],
  ["/services/ai-consulting-malaysia", "I'd like to talk about AI consulting for my business", "your AI Consulting page"],
  ["/services/training-education", "I'd like to ask about AI and Claude training for my team", "your Training page"],
  ["/services/conversational-ai", "I'd like to talk about an AI chatbot for my business", "your Conversational AI page"],
  ["/services/seo-services-malaysia", "I'd like to talk about AI SEO for my website", "your AI SEO page"],
  ["/services/ai-seo-kuala-lumpur", "I'd like to talk about AI SEO for my KL business", "your AI SEO Kuala Lumpur page"],
  ["/services/website-development", "I'd like to talk about building a website", "your Website Development page"],
  ["/services/vibe-code-rescue", "I need help rescuing an AI-built app", "your Vibe Code Rescue page"],
  ["/services/claude-managed-service-malaysia", "I'd like to ask about your Claude managed service", "your Claude Managed Service page"],
  ["/services", "I'd like to talk about your services", "your Services page"],
  ["/anthropic-claude-partner-malaysia-singapore", "I'd like to talk about Claude for my company", "your Claude Partner page"],
  ["/partners/anthropic-claude-partner", "I'd like to talk about Claude for my company", "your Claude Partner page"],
  ["/partners/openai-partner", "I'd like to talk about ChatGPT or the OpenAI API for my company", "your OpenAI Partner page"],
  ["/guides/malaysia-budget-2027", "I'd like to talk about an AI project for 2027", "the Budget 2027 guide"],
  ["/guides/chatgpt-plans-malaysia", "I need help choosing a ChatGPT plan for my team", "the ChatGPT plans guide"],
  ["/guides/claude-plans-malaysia", "I need help choosing a Claude plan for my team", "the Claude plans guide"],
  ["/guides/how-to-use-claude-malaysia", "I'd like help getting Claude for my team in Malaysia", "the Claude Malaysia guide"],
  ["/guides/claude-cowork", "I'd like to ask about rolling out Claude Cowork", "the Claude Cowork guide"],
  ["/guides", "I have a question about Claude", "your guides"],
  ["/tools/ai-visibility-check", "I'd like a free AI visibility check for my website", "the AI visibility check page"],
  ["/tools/claude-cost-calculator", "I'd like help estimating Claude costs for my team", "the Claude cost calculator"],
  ["/academy/chatgpt-training", "I'd like to ask about ChatGPT training for my team", "your ChatGPT training page"],
  ["/academy", "I'd like to ask about Claude training", "your Academy page"],
  ["/enterprise/dgx-spark-malaysia", "I'd like a quote for NVIDIA DGX Spark", "your DGX Spark page"],
  ["/enterprise/dgx-spark-tender-malaysia", "I'd like to ask about DGX Spark for a tender", "your DGX Spark tender page"],
  ["/enterprise/microsoft-malaysia", "I'd like to ask about Microsoft licensing", "your Microsoft page"],
  ["/enterprise/red-hat-malaysia", "I'd like to ask about Red Hat subscriptions", "your Red Hat page"],
  ["/enterprise/sonarqube-malaysia", "I'd like to ask about SonarQube", "your SonarQube page"],
  ["/enterprise/nvidia-malaysia", "I'd like to ask about NVIDIA GPUs", "your NVIDIA page"],
  ["/enterprise", "I'd like to ask about enterprise software or hardware", "your Enterprise page"],
  ["/solutions/agent-use-cases", "I'd like to talk about an AI agent use case", "your Agent Use Cases page"],
  ["/solutions/agent-rescue", "I need help rescuing an AI agent project", "your Agent Rescue page"],
  ["/solutions", "I'd like to talk about an AI agent for my business", "your Solutions page"],
  ["/contact", "I'd like to talk about a project", "your Contact page"],
  ["/blog", "I have a question after reading your blog", "your blog"],
];

/** Strip /ms or /zh locale prefix and trailing slash. "/" stays "/". */
function normalisePath(pathname: string): string {
  const stripped = pathname.replace(/^\/(ms|zh)(?=\/|$)/, "") || "/";
  return stripped.length > 1 ? stripped.replace(/\/+$/, "") : "/";
}

/** Explicit demo placements; other pages keep their own enquiry context. */
export function ezychatDemoSource(pathname: string): string | undefined {
  const sources: Record<string, string> = {
    "/": "your homepage",
    "/contact": "your Contact page",
    "/services/conversational-ai": "your Conversational AI page",
    "/blog/why-whatsapp-ai-assistants-next-big-thing-business-2026": "your WhatsApp AI assistants article",
  };
  return sources[normalisePath(pathname)];
}

export function ezychatDemoMessage(source: string): string {
  return whatsappMessage("I'd like to try the EzyChat AI assistant on WhatsApp", source);
}

/**
 * Pre-filled message for the floating WhatsApp button on a given page.
 * Falls back to the page title (or path) when the route is not mapped.
 */
export function floatingWhatsappMessage(pathname: string, pageTitle?: string): string {
  const path = normalisePath(pathname || "/");
  if (path === "/") {
    return whatsappMessage("I'd like to talk about AI for my business", "your homepage");
  }
  const match = FLOATING_MESSAGES.filter(
    ([prefix]) => path === prefix || path.startsWith(`${prefix}/`),
  ).sort((a, b) => b[0].length - a[0].length)[0];
  if (match) return whatsappMessage(match[1], match[2]);

  const title = pageTitle?.split(/\s[|–—-]\s/)[0]?.trim();
  return whatsappMessage("I have a question", title || path);
}
